import { useState, useCallback } from 'react';
import { DATE_WARNING_TYPES } from '../constants/date-warning-types';

class CustomWarning extends Error {
  constructor(message, type, result, metadata = {}) {
    super(message);
    this.name = 'WarningError';
    this.type = type;
    this.result = result;
    this.metadata = metadata;
  }
}

const useCustomWarnings = (shouldKeepWarning) => {
  const [warnings, setWarnings] = useState([]);

  const addWarning = useCallback((message, type = DATE_WARNING_TYPES.WEEKEND, metadata = {}) => {
    setWarnings((prev) => {
      const newWarnings = [
        ...prev,
        {
          message,
          type,
          metadata,
        },
      ];

      return newWarnings;
    });
  }, []);

  const clearWarnings = useCallback((type) => {
    if (type) {
      setWarnings((prev) => prev.filter((warning) => warning.type !== type));
    } else {
      setWarnings([]);
    }
  }, []);

  const cleanupObsoleteWarnings = useCallback(() => {
    setWarnings((prevWarnings) => {
      const filteredWarnings = prevWarnings.filter((
        warning,
      ) => shouldKeepWarning(warning));

      return filteredWarnings;
    });
  }, [warnings, shouldKeepWarning]);

  const executeWithWarningHandling = useCallback((operation, warningHandler) => {
    try {
      const result = operation();
      return result;
    } catch (error) {
      if (error instanceof CustomWarning) {
        addWarning(error.message, error.type, error.metadata);

        if (warningHandler) {
          const handlerResult = warningHandler(error.result);
          return handlerResult;
        }
        return error.result;
      }
      throw error;
    } finally {
      cleanupObsoleteWarnings();
    }
  }, [addWarning, cleanupObsoleteWarnings]);

  const throwWarning = useCallback((message, type, result = null, metadata = {}) => {
    throw new CustomWarning(message, type, result, metadata);
  }, []);

  const getFirstWarningByType = useCallback(
    (type) => warnings.find((warning) => warning.type === type),
    [warnings],
  );

  return {
    clearWarnings,
    executeWithWarningHandling,
    throwWarning,
    getFirstWarningByType,
  };
};

export default useCustomWarnings;
